import { html, nothing } from 'lit';
type LandingLocale = string;
import type { EditorLocalizationFlowController } from '../../localization.js';

const localeLabel = (locale: string) => locale.toUpperCase();

export interface EditorLocalizationFlowViewOptions {
  flow: EditorLocalizationFlowController;
  localeOrder: readonly LandingLocale[];
  highlightRect?: { left: number; top: number; width: number; height: number } | null;
  onDismissPrompt: () => void;
  onStart: () => void;
  onCandidateChange: (value: string) => void;
  onMediaUpload: (event: Event) => void;
  onRetry: () => void;
  onPrevious: () => void;
  onSkip: () => void;
  onSkipLocale: () => void;
  onConfirm: () => void;
  onExit: () => void;
  onRetrySave: () => void;
}

const stepProgress = (
  flow: EditorLocalizationFlowController,
  localeOrder: readonly LandingLocale[],
) => {
  const round = flow.round!;
  return localeOrder.map(locale => {
    if (locale === round.scope.originLocale) return html`<span class="is-complete">${localeLabel(locale)} ✓</span>`;
    const localeSteps = round.steps.filter(step => step.locale === locale);
    const done = localeSteps.filter(step => step.status !== 'pending').length;
    return html`<span class=${done === localeSteps.length ? 'is-complete' : round.steps[round.activeIndex]?.locale === locale ? 'is-active' : ''}>
      ${localeLabel(locale)} ${done}/${localeSteps.length}
    </span>`;
  });
};

const candidateStatus = (kind: 'source' | 'manual' | 'current' | 'already-current', hasError: boolean) => {
  if (hasError) return 'Fehler';
  if (kind === 'manual') return 'Manuelle Bearbeitung';
  if (kind === 'already-current') return 'Bereits aktuell';
  if (kind === 'current') return 'Bisheriger Inhalt';
  return 'Quellwert';
};

export const renderEditorLocalizationFlow = ({
  flow,
  localeOrder,
  highlightRect,
  onDismissPrompt,
  onStart,
  onCandidateChange,
  onMediaUpload,
  onRetry,
  onPrevious,
  onSkip,
  onSkipLocale,
  onConfirm,
  onExit,
  onRetrySave,
}: EditorLocalizationFlowViewOptions) => {
  const pending = flow.queue[0];
  const round = flow.round;
  if (!round && !pending) return nothing;

  if (!round && pending) {
    const targets = localeOrder.filter(locale => locale !== pending.originLocale);
    return html`
      <section
        class="vp-editor-localization-prompt"
        role="dialog"
        aria-labelledby="vp-localization-prompt-title"
        @keydown=${(event: KeyboardEvent) => {
          if (event.key !== 'Escape') return;
          event.preventDefault();
          onDismissPrompt();
        }}
      >
        <span class="icon-[material-symbols--translate] vp-editor-localization-icon" aria-hidden="true"></span>
        <div class="vp-editor-localization-copy">
          <h2 id="vp-localization-prompt-title">Änderungen in „${pending.scopeLabel}“</h2>
          <p>
            ${pending.isNewScope ? `Neuer Bereich · ${pending.changedFields.size} Inhalte prüfen` : `${pending.changedFields.size} Inhalte in ${localeLabel(pending.originLocale)} geändert`}
          </p>
          <div class="vp-editor-localization-locales" aria-label="Zielsprachen">
            ${targets.map(locale => html`<span>${localeLabel(locale)}</span>`)}
          </div>
        </div>
        <div class="vp-editor-localization-actions">
          <button type="button" @click=${onDismissPrompt}>Nicht jetzt</button>
          <button type="button" class="is-primary" autofocus @click=${onStart}>Sprachen bearbeiten</button>
        </div>
      </section>
    `;
  }

  const isFinishing = round!.phase === 'saving' || round!.phase === 'save-error' || round!.phase === 'complete';
  const step = isFinishing ? undefined : round!.steps[round!.activeIndex];
  const isBusy = round!.phase === 'preparing' || round!.phase === 'saving';
  const isText = step?.field.kind === 'text';
  const isTrack = step?.field.kind === 'track';
  const isMedia = step?.field.kind === 'image' || step?.field.kind === 'video' || isTrack;
  const confirmed = round!.steps.filter(item => item.status === 'confirmed').length;
  const skipped = round!.steps.filter(item => item.status === 'skipped').length;
  const localeSteps = step ? round!.steps.filter(item => item.locale === step.locale) : [];
  const localeStepIndex = step ? localeSteps.findIndex(item => item.id === step.id) + 1 : 0;

  return html`
    ${step && highlightRect ? html`
      <div
        class="vp-editor-localization-target"
        style=${`left:${highlightRect.left}px;top:${highlightRect.top}px;width:${highlightRect.width}px;height:${highlightRect.height}px;`}
        aria-hidden="true"
      >
        <span>${localeLabel(step.locale)} · ${localeStepIndex}/${localeSteps.length}</span>
      </div>
    ` : nothing}
    <section
      class="vp-editor-localization-stepper"
      role="dialog"
      aria-modal="false"
      aria-labelledby="vp-localization-stepper-title"
      aria-busy=${String(isBusy)}
    >
      <header>
        <div>
          <p class="vp-editor-kicker">Sprachrunde · ${round!.scope.scopeLabel}</p>
          <h2 id="vp-localization-stepper-title">${step?.field.label ?? 'Änderungen speichern'}</h2>
        </div>
        <button type="button" class="vp-editor-localization-exit" @click=${onExit}>
          Sprachrunde beenden
        </button>
      </header>

      <div class="vp-editor-localization-progress" aria-label="Fortschritt">
        ${stepProgress(flow, localeOrder)}
      </div>

      ${step ? html`
        <div class="vp-editor-localization-meta">
          <span>Feld ${round!.activeIndex + 1} von ${round!.steps.length}</span>
          <span>${localeLabel(step.locale)} · ${localeStepIndex}/${localeSteps.length}</span>
          <span class=${round!.error ? 'is-error' : 'is-status'}>${candidateStatus(round!.candidateKind, Boolean(round!.error))}</span>
        </div>

        ${round!.phase === 'preparing' ? html`
          <div class="vp-editor-localization-loading" role="status">
            <span class="vp-editor-spinner" aria-hidden="true"></span>
            Zielinhalt wird vorbereitet …
          </div>
        ` : nothing}

        ${round!.error ? html`
          <div class="vp-editor-localization-error" role="alert">
            <strong>Inhalt konnte nicht vorbereitet werden.</strong>
            <span>${round!.error}</span>
            <button type="button" @click=${onRetry}>Erneut laden</button>
          </div>
        ` : nothing}

        ${round!.phase === 'editing' && isText ? html`
          <div class="vp-editor-localization-compare">
            <div><span>Ausgangstext in ${localeLabel(round!.scope.originLocale)}</span><p>${step.field.currentValue || 'Leer'}</p></div>
            <div><span>Bisher in ${localeLabel(step.locale)}</span><p>${round!.previousTargetValue || 'Leer'}</p></div>
          </div>
          <p class="vp-editor-localization-hint">Passe den markierten Zieltext direkt an seiner Position auf der Canvas an. Erst „Übernehmen“ schreibt die Änderung in den Draft.</p>
        ` : nothing}

        ${round!.phase === 'editing' && !isText ? html`
          <div class="vp-editor-localization-compare">
            <div><span>Bisher in ${localeLabel(step.locale)}</span><p>${round!.previousTargetValue || 'Leer'}</p></div>
            <div><span>Vorschlag aus ${localeLabel(round!.scope.originLocale)}</span><p>${isTrack ? 'Sprachspezifische Datei erforderlich' : round!.candidate || 'Leer'}</p></div>
          </div>
          ${isTrack ? html`
            <p class="vp-editor-localization-hint">Untertitel werden nie automatisch zwischen Sprachen kopiert. Hinterlege die passende WebVTT-Datei am markierten Element oder überspringe diesen Schritt.</p>
          ` : nothing}
          ${isMedia ? html`
            <div class="vp-editor-localization-choice-row">
              ${!isTrack ? html`<button type="button" @click=${() => onCandidateChange(step.field.currentValue)}>Quellmedium übernehmen</button>` : nothing}
              <button type="button" @click=${() => onCandidateChange(round!.previousTargetValue)}>Zielmedium behalten</button>
              <label class="vp-editor-localization-upload">
                <span class="icon-[material-symbols--upload]" aria-hidden="true"></span> Datei hochladen
                <input type="file" accept=${step.field.kind === 'image' ? 'image/*' : step.field.kind === 'video' ? 'video/*' : '.vtt,text/vtt'} @change=${onMediaUpload} />
              </label>
            </div>
            <label class="vp-editor-localization-field">
              Medienbibliothek
              <select .value=${round!.candidate} @change=${(event: Event) => onCandidateChange((event.currentTarget as HTMLSelectElement).value)}>
                <option value="">Medium auswählen …</option>
                ${round!.mediaOptions?.map(option => html`<option value=${option.value}>${option.label}</option>`) ?? nothing}
              </select>
            </label>
          ` : html`
            <label class="vp-editor-localization-field">
              Zu übernehmender Wert
              <input .value=${round!.candidate} @input=${(event: InputEvent) => onCandidateChange((event.currentTarget as HTMLInputElement).value)} />
            </label>
          `}
        ` : nothing}
      ` : html`
        <div class="vp-editor-localization-summary" role="status">
          <span class="icon-[material-symbols--check-circle-outline]" aria-hidden="true"></span>
          <strong>${confirmed} übernommen · ${skipped} übersprungen</strong>
          <p>${round!.phase === 'save-error' ? round!.error : round!.phase === 'complete' ? 'Gespeichert. Du kehrst gleich zu deiner vorherigen Stelle zurück.' : 'Der aktuelle Draft wird gespeichert …'}</p>
          ${round!.phase === 'save-error' ? html`<button type="button" class="is-primary" @click=${onRetrySave}>Erneut speichern</button>` : nothing}
        </div>
      `}

      ${step ? html`
        <footer>
          <button type="button" @click=${onPrevious} ?disabled=${round!.activeIndex === 0 || isBusy}>Zurück</button>
          <button type="button" @click=${onSkip} ?disabled=${isBusy}>Überspringen</button>
          <button type="button" @click=${onSkipLocale} ?disabled=${isBusy}>Rest dieser Sprache überspringen</button>
          <button type="button" class="is-primary" @click=${onConfirm} ?disabled=${isBusy || (isTrack && !round!.candidate)}>
            ${round!.activeIndex === round!.steps.length - 1 ? 'Übernehmen & speichern' : 'Übernehmen & weiter'}
          </button>
        </footer>
      ` : nothing}
    </section>
  `;
};
